import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../services/setupClientSubmaster/apiCliente";

const INTERNAL_ACCESS_KEY = "chavedeacessomaster#2528";

const permissionOptions = [
  ["canStockEntry", "Entrada no estoque"],
  ["canStockEdit", "Editar item do estoque"],
  ["canStockDelete", "Apagar item/estoque"],
  ["canEmployeeCreate", "Criar funcionário"],
  ["canEmployeeEdit", "Editar funcionário"],
  ["canEmployeeDelete", "Apagar funcionário"],
  ["canEpiCreate", "Criar EPI"],
  ["canEpiEdit", "Editar EPI"],
  ["canEpiDelete", "Apagar EPI"],
  ["canCompanyCreate", "Criar empresa"],
  ["canCompanyEdit", "Editar empresa"],
  ["canCompanyDelete", "Apagar empresa"],
] as const;

type PermissionName = typeof permissionOptions[number][0];
type Permissions = Record<PermissionName, boolean>;

type WebUser = Permissions & {
  id: number;
  username: string;
  name?: string | null;
  isMaster: boolean;
  isActive: boolean;
};

const emptyPermissions = () => Object.fromEntries(
  permissionOptions.map(([key]) => [key, false])
) as Permissions;

const emptyForm = () => ({
  id: 0,
  username: "",
  name: "",
  password: "",
  isMaster: false,
  isActive: true,
  permissions: emptyPermissions(),
});

export default function WebUserManagementPanel({
  submasterId,
  companyName,
}: {
  submasterId: number;
  companyName: string;
}) {
  const [users, setUsers] = useState<WebUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient().post("/internal/webUsers/list", {
        accessKey: INTERNAL_ACCESS_KEY,
        submasterId,
      });
      setUsers(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível carregar os usuários web.");
    } finally {
      setLoading(false);
    }
  }, [submasterId]);

  useEffect(() => { void loadUsers(); }, [loadUsers]);

  const openNew = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (user: WebUser) => {
    setForm({
      id: user.id,
      username: user.username,
      name: user.name || "",
      password: "",
      isMaster: user.isMaster,
      isActive: user.isActive,
      permissions: Object.fromEntries(
        permissionOptions.map(([key]) => [key, user.isMaster || user[key] === true])
      ) as Permissions,
    });
    setShowForm(true);
  };

  const ensureMaster = async () => {
    try {
      setSaving(true);
      const response = await apiClient().post("/internal/webUsers/ensureMaster", {
        accessKey: INTERNAL_ACCESS_KEY,
        submasterId,
      });
      if (!response?.data?.res) throw new Error(response?.data?.message);
      toast.success("Usuário master criado com a senha atual da empresa.");
      await loadUsers();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Não foi possível criar o master.");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const username = form.username.trim().toLowerCase();
    if (!username) return toast.warning("Informe o nome de usuário.");
    if (!/^[a-z0-9._-]{3,80}$/.test(username)) {
      return toast.warning("O usuário deve ter pelo menos 3 caracteres e usar somente letras sem acento, números, ponto, hífen ou sublinhado.");
    }
    if ((!form.id || form.password.length > 0) && form.password.length < 6) {
      return toast.warning("A senha deve ter pelo menos 6 caracteres.");
    }
    try {
      setSaving(true);
      const response = await apiClient().post("/internal/webUsers/save", {
        accessKey: INTERNAL_ACCESS_KEY,
        submasterId,
        userId: form.id || undefined,
        username,
        name: form.name,
        password: form.password || undefined,
        isActive: form.isActive,
        permissions: form.permissions,
      });
      if (!response?.data?.res) throw new Error(response?.data?.message);
      toast.success(form.id ? "Usuário atualizado." : "Usuário criado.");
      setShowForm(false);
      setForm(emptyForm());
      await loadUsers();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Não foi possível salvar o usuário.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (user: WebUser) => {
    if (user.isMaster || !window.confirm(`Excluir o usuário ${user.username}?`)) return;
    try {
      const response = await apiClient().post("/internal/webUsers/delete", {
        accessKey: INTERNAL_ACCESS_KEY,
        submasterId,
        userId: user.id,
      });
      if (!response?.data?.res) throw new Error(response?.data?.message);
      toast.success("Usuário excluído.");
      await loadUsers();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Não foi possível excluir o usuário.");
    }
  };

  return (
    <section className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(18,49,58,0.09)" }}>
      <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 mb-3">
        <div>
          <h4 className="h5 fw-bold mb-1">Usuários de acesso web</h4>
          <p className="text-muted mb-0">Login e permissões individuais para {companyName}.</p>
        </div>
        <div className="d-flex flex-wrap justify-content-md-end gap-2 align-self-start flex-shrink-0">
          {users.length === 0 && (
            <button
              className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center text-nowrap flex-shrink-0"
              style={{ width: "auto", minWidth: "max-content", height: 38, padding: "0 0.85rem", whiteSpace: "nowrap" }}
              disabled={saving}
              onClick={ensureMaster}
            >
              <i className="bi bi-shield-lock me-1" /> Criar master
            </button>
          )}
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center text-nowrap flex-shrink-0"
            style={{ minWidth: "max-content", height: 38, padding: "0 0.85rem", whiteSpace: "nowrap" }}
            onClick={openNew}
          >
            <i className="bi bi-person-plus me-1" /> Novo usuário
          </button>
        </div>
      </div>

      {users.length === 0 && !loading && (
        <div className="alert alert-warning">
          Sem usuário web: esta empresa continua entrando somente com CNPJ e senha.
        </div>
      )}

      {users.length > 0 && (
        <div className="table-responsive mb-3">
          <table className="table table-sm align-middle">
            <thead><tr><th>Usuário</th><th>Nome</th><th>Tipo</th><th>Status</th><th className="text-end">Ações</th></tr></thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.username}</strong></td>
                  <td>{user.name || "—"}</td>
                  <td>{user.isMaster ? <span className="badge bg-primary">Master</span> : "Personalizado"}</td>
                  <td><span className={`badge ${user.isActive ? "bg-success" : "bg-secondary"}`}>{user.isActive ? "Ativo" : "Inativo"}</span></td>
                  <td className="text-end">
                    <button className="btn btn-outline-primary btn-sm me-2" onClick={() => openEdit(user)}>Editar</button>
                    {!user.isMaster && <button className="btn btn-outline-danger btn-sm" onClick={() => remove(user)}>Excluir</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="p-3 rounded-3 bg-white border">
          <h5>{form.id ? "Editar usuário" : "Novo usuário"}</h5>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Usuário</label>
              <input className="form-control" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} />
              <small className="text-muted">Mínimo de 3 caracteres, sem espaços ou acentos.</small>
            </div>
            <div className="col-md-4"><label className="form-label">Nome</label><input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-md-4">
              <label className="form-label">{form.id ? "Nova senha (opcional)" : "Senha"}</label>
              <input type="password" className="form-control" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <small className="text-muted">{form.id ? "Deixe em branco para manter a atual; ao alterar, use pelo menos 6 caracteres." : "Use pelo menos 6 caracteres."}</small>
            </div>
          </div>
          {!form.isMaster && <div className="form-check form-switch my-3"><input className="form-check-input" type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /><label className="form-check-label">Usuário ativo</label></div>}
          <div className="row g-2 mt-2">
            {permissionOptions.map(([key, label]) => (
              <div className="col-12 col-md-6 col-xl-4" key={key}>
                <label className="d-flex align-items-center gap-2 border rounded p-2 h-100">
                  <input type="checkbox" checked={form.isMaster || form.permissions[key]} disabled={form.isMaster} onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, [key]: e.target.checked } })} />
                  <span>{label}</span>
                </label>
              </div>
            ))}
          </div>
          {form.isMaster && <div className="alert alert-info mt-3 mb-0">O usuário master possui todas as permissões e não pode ser desativado.</div>}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-outline-secondary" disabled={saving} onClick={() => setShowForm(false)}>Cancelar</button>
            <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? "Salvando..." : "Salvar usuário"}</button>
          </div>
        </div>
      )}
    </section>
  );
}
